/**
 * Settings — minimal real page. Theme toggle + identity card + sign out.
 *
 * Replaces the deleted `SettingsReports.tsx` mock (8 fake tabs of placeholder
 * config). Anything that needs to live in Settings later (notifications,
 * retention, billing) should be added here as discrete sections only once
 * the underlying data model is wired.
 */

import { useAuthKit } from '@/contexts/AuthProvider';
import { modChord } from '@/components/chrome';
import { useTweaks } from '@/hooks/useTweaks';
import { useProjects } from '@/hooks/useProjects';
import { useRole, isAdmin as roleIsAdmin } from '@/hooks/useRole';
import { ProjectAgentPicker } from './ProjectAgentPicker';
import { MpServiceConfigForm } from './MpServiceConfigForm';
import type { Site, Toast } from '@/data/types';

type SettingsProps = { toast: (t: Omit<Toast, 'id'>) => void };

export function Settings({ toast }: SettingsProps) {
  const { user, signOut } = useAuthKit();
  const { tweaks, update } = useTweaks();
  const { sites } = useProjects();
  const normalizedRole = useRole();
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || user?.email || '—';
  const isAdmin = roleIsAdmin(normalizedRole);
  const currentSite: Site | undefined = sites[tweaks.siteIndex] ?? sites[0];

  return (
    <>
      <div className="page-head">
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Settings</h1>
          <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 4 }}>
            Personal preferences + account info.
          </div>
        </div>
      </div>

      <div className="page-body" style={{ display: 'grid', gap: 12, maxWidth: 720 }}>
        <section className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
            Appearance
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>Theme</div>
              <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>
                Switch between dark and light. {modChord('L')} toggles from anywhere.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                className={'btn sm ' + (tweaks.theme === 'dark' ? '' : 'ghost')}
                onClick={() => update({ theme: 'dark' })}
              >
                Dark
              </button>
              <button
                type="button"
                className={'btn sm ' + (tweaks.theme === 'light' ? '' : 'ghost')}
                onClick={() => update({ theme: 'light' })}
              >
                Light
              </button>
            </div>
          </div>
        </section>

        <section className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
            Units
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>Measurement system</div>
              <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>
                Distances, heights and speeds in metres or feet. Saved on this device.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                className={'btn sm ' + (tweaks.units === 'metric' ? '' : 'ghost')}
                onClick={() => update({ units: 'metric' })}
              >
                Metric
              </button>
              <button
                type="button"
                className={'btn sm ' + (tweaks.units === 'imperial' ? '' : 'ghost')}
                onClick={() => update({ units: 'imperial' })}
              >
                Imperial
              </button>
            </div>
          </div>
        </section>

        {isAdmin ? (
          <section className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Site
              {currentSite ? (
                <span style={{ color: 'var(--muted)', textTransform: 'none', letterSpacing: 0, marginLeft: 8 }}>
                  · <code style={{ fontSize: 11 }}>{currentSite.projectName}</code>
                </span>
              ) : null}
            </div>
            {currentSite ? (
              <ProjectAgentPicker site={currentSite} sites={sites} toast={toast} />
            ) : (
              <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                Pick a site in the topbar to configure its onboarding agent.
              </div>
            )}
          </section>
        ) : null}

        {isAdmin ? (
          <section className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Organization · onboarding sessions
            </div>
            <MpServiceConfigForm toast={toast} />
          </section>
        ) : null}

        <section className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
            Account
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 8, columnGap: 12, fontSize: 12.5 }}>
            <div style={{ color: 'var(--muted)' }}>Name</div>
            <div>{fullName}</div>
            <div style={{ color: 'var(--muted)' }}>Email</div>
            <div className="mono">{user?.email ?? '—'}</div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn sm" onClick={() => signOut?.()}>
              Sign out
            </button>
          </div>
        </section>
      </div>
    </>
  );
}
